import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Globe2, CheckCircle2, XCircle, Plus } from "lucide-react";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";

export default function SitesPage() {
  const [sites, setSites] = useState([]);
  const [newDomain, setNewDomain] = useState("");
  const load = () => api.get("/sites").then(r => setSites(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newDomain) return;
    try { await api.post("/sites", { domain: newDomain }); setNewDomain(""); toast.success("Site ajouté"); load(); }
    catch { toast.error("Erreur"); }
  };
  const verify = async (id, method) => {
    try { await api.post(`/sites/${id}/verify`, null, { params: { method } }); toast.success("Propriété vérifiée"); load(); }
    catch { toast.error("Erreur"); }
  };
  const setFreq = async (id, freq) => {
    try { await api.patch(`/sites/${id}`, { recrawl_frequency: freq }); toast.success("Fréquence mise à jour"); load(); }
    catch { toast.error("Erreur"); }
  };
  return (
    <div className="space-y-8" data-testid="sites-page">
      <div>
        <p className="eyebrow mb-1">Configuration</p>
        <h1 className="text-3xl sm:text-4xl font-serif tracking-tight">Sites surveillés</h1>
      </div>
      <div className="hairline p-6 rounded-lg flex gap-3">
        <Input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="exemple.fr" className="rounded-lg h-11 border-border flex-1" data-testid="sites-new-input" />
        <Button onClick={create} className="rounded-lg bg-primary text-primary-foreground" data-testid="sites-new-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter</Button>
      </div>
      <div className="hairline">
        {sites.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">Aucun site.</p>}
        {sites.map((s, i) => (
          <div key={s.id} className={`p-5 grid md:grid-cols-[auto_1fr_auto_auto_auto] gap-4 items-center ${i > 0 ? "border-t border-border" : ""}`} data-testid={`site-${s.id}`}>
            <Globe2 className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-serif text-lg">{s.domain}</p>
              <p className="font-mono text-[11px] text-muted-foreground mt-1">
                {s.verified ? <>Vérifié · méthode {s.verification_method}</> : <>Token: <span className="select-all">empco-verify={s.verification_token}</span></>}
              </p>
            </div>
            <span className={`px-2 py-1 text-[10px] font-mono uppercase rounded-lg ${s.verified ? "tag-green" : "tag-orange"}`}>
              {s.verified ? "vérifié" : "en attente"}
            </span>
            {s.verified && (
              <Select value={s.recrawl_frequency || "weekly"} onValueChange={(f) => setFreq(s.id, f)}>
                <SelectTrigger className="w-40 rounded-lg" data-testid={`freq-select-${s.id}`}><SelectValue placeholder="Fréquence" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Chaque semaine</SelectItem>
                  <SelectItem value="monthly">Chaque mois</SelectItem>
                </SelectContent>
              </Select>
            )}
            {!s.verified && (
              <Select onValueChange={(m) => verify(s.id, m)}>
                <SelectTrigger className="w-40 rounded-lg" data-testid={`verify-select-${s.id}`}><SelectValue placeholder="Vérifier via…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dns_txt">DNS TXT</SelectItem>
                  <SelectItem value="meta_html">Meta HTML</SelectItem>
                  <SelectItem value="well_known">/.well-known</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
